/**
 * KloutScoreBadge Component
 *
 * Displays a creator's Kloutscore with provenance awareness:
 * - Verified (API-backed) → gradient badge with checkmark
 * - Manual (self-reported) → outlined badge, no checkmark
 * - Pending (no score) → pending state
 */
import React from 'react';
import { CheckCircle, Star, ShieldAlert } from 'lucide-react';
import { cn, resolveKloutscoreProvenance } from '@bright-web-skeleton/ui';

interface KloutScoreBadgeProps {
  kloutscore: number | null;
  verifiedAt: string | null;
  source?: string | null;
  size?: 'sm' | 'md' | 'lg';
  showPending?: boolean;
  className?: string;
}

export const KloutScoreBadge: React.FC<KloutScoreBadgeProps> = ({
  kloutscore,
  verifiedAt,
  source,
  size = 'md',
  showPending = false,
  className,
}) => {
  const provenance = resolveKloutscoreProvenance(kloutscore, source, verifiedAt);
  const isVerified = provenance === 'verified';
  const isManual = provenance === 'self-reported';
  const isUnverified = provenance === 'unverified';

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-3 py-1 text-sm gap-1.5',
    lg: 'px-4 py-1.5 text-base gap-2',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Verified: full gradient badge
  if (isVerified && kloutscore !== null) {
    return (
      <div
        data-kloutscore-provenance="verified"
        className={cn(
          'inline-flex items-center rounded-full font-semibold',
          'bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500',
          'text-white shadow-sm',
          sizeClasses[size],
          className,
        )}
      >
        <Star className={cn(iconSizes[size], 'fill-current')} />
        <span>Kloutscore {kloutscore}</span>
        <CheckCircle className={cn(iconSizes[size])} />
        <span>Verified</span>
      </div>
    );
  }

  // Manual: score exists but self-reported
  if (isManual) {
    return (
      <div
        data-kloutscore-provenance="self-reported"
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          'border border-amber-300 text-amber-700 bg-amber-50/50',
          sizeClasses[size],
          className,
        )}
      >
        <Star className={cn(iconSizes[size], 'text-amber-500')} />
        <span>Kloutscore {kloutscore}</span>
        <ShieldAlert className={cn(iconSizes[size], 'text-amber-400')} />
        <span className="text-[10px] opacity-70">Self-reported</span>
      </div>
    );
  }

  if (isUnverified && kloutscore !== null) {
    return (
      <div
        data-kloutscore-provenance="unverified"
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          'border border-slate-300 text-slate-700 bg-slate-50/50',
          sizeClasses[size],
          className,
        )}
      >
        <Star className={cn(iconSizes[size], 'text-slate-500')} />
        <span>Kloutscore {kloutscore}</span>
        <ShieldAlert className={cn(iconSizes[size], 'text-slate-400')} />
        <span className="text-[10px] opacity-70">Source unverified</span>
      </div>
    );
  }

  // Pending
  if (showPending && kloutscore === null) {
    return (
      <div
        data-kloutscore-provenance="pending"
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          'border border-pink-300 text-pink-600 bg-pink-50/50',
          sizeClasses[size],
          className,
        )}
      >
        <Star className={cn(iconSizes[size], 'text-pink-400')} />
        <span>Kloutscore pending</span>
      </div>
    );
  }

  return null;
};

export default KloutScoreBadge;
